import { useEffect, useState } from 'react'
import Image from 'next/image'
import clsx from 'clsx'

import Link from '@/atoms/Link/Link'

/* assets */
import { ReactComponent as ArrowUp } from 'public/assets/icons/arrow-up.svg'
import { ReactComponent as ChevronRightIcon } from 'public/assets/icons/chevron-right-bold.svg'

/* styles */
import styles from './_.module.css'

interface Props {
  data: {
    slug: string
    heroTitle: string
    customerLogo: StrapiImageFile
    customerLogoWhite: StrapiImageFile
    thumbnailImage: StrapiImageFile
    statisticsList: StrapiBlockComponent[]
  }[]
}

function FeaturedCaseStudies({ data }: Props) {
  const [selectedData, setSelectedData] = useState<Props['data'][0] | null>(
    null
  )
  const [activeSlug, setActiveSlug] = useState<string | null>(null)

  const handleClick = (slug: string) => {
    setActiveSlug(activeSlug !== slug ? slug : activeSlug)
    const foundData = data.find(item => item.slug === slug)
    setSelectedData(foundData || null)
  }

  useEffect(() => {
    if (data.length > 0) {
      setSelectedData(data[0])
    } else {
      setSelectedData(null)
    }
  }, [data])

  return (
    <section className="font-heading text-white  bg-blue-web-90 | py-12 lg:py-16">
      <div className="container">
        {/* Featured CS Customer Logos (mob) */}
        <div className="block">
          <div className="flex justify-center items-center gap-4 | text-gray-50 | mb-14">
            <ArrowUp className="rotate-180" />
            Click on the logos to view the case study
          </div>

          <div className="flex md:justify-center items-center overflow-auto gap-10 whitespace-nowrap | mb-20 md:mb-10">
            {data.map(item => (
              <button
                key={item.heroTitle}
                onClick={() => handleClick(item.slug)}
                className="flex items-center shrink-0"
              >
                {item.customerLogoWhite.url ? (
                  <Image
                    src={item.customerLogoWhite.url}
                    alt={item.customerLogoWhite.alternativeText ?? ''}
                    width={item.customerLogoWhite.width}
                    height={item.customerLogoWhite.height}
                    className={clsx(
                      activeSlug === item.slug ? '' : 'opacity-60',
                      'transform delay-100 ease-linear hover:opacity-100 | w-24 md:w-48 md:px-9 md:py-3'
                    )}
                  />
                ) : null}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 | mb-5 order-2">
          <div className="w-8 h-8 bg-yellow-50 rounded-md" />
          <div className="text-small font-semibold">FEATURED CASE STUDY</div>
        </div>
        {selectedData ? (
          <div
            key={selectedData.slug}
            className={clsx(
              'grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 | md:mb-24',
              styles.animate
            )}
          >
            <div className="lg:col-span-6 order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-semibold | mb-5">
                {selectedData.heroTitle}
              </h2>
              <hr className="hidden md:block text-gray-80 | mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-14 | mb-10 md:mb-16">
                {selectedData.statisticsList.slice(0, 2).map(stat => (
                  <div key={stat.id}>
                    <p
                      className="text-4xl md:text-6xl text-green-50 | mb-2 md:mb-3 w-full"
                      dangerouslySetInnerHTML={{ __html: stat.title ?? '' }}
                    />
                    <p
                      className="text-lg text-gray-40"
                      dangerouslySetInnerHTML={{
                        __html: stat.description ?? ''
                      }}
                    />
                  </div>
                ))}
              </div>
              <Link
                href={selectedData.slug}
                className="text-xl | flex items-center gap-2"
              >
                Read Case Study
                <div className="bg-white p-2 rounded-full">
                  <ChevronRightIcon />
                </div>
              </Link>
            </div>
            <div className="lg:col-start-8 lg:col-end-13 order-1 md:order-2">
              <Image
                src={selectedData.thumbnailImage.url}
                alt={selectedData.thumbnailImage.alternativeText ?? ''}
                width={selectedData.thumbnailImage.width}
                height={selectedData.thumbnailImage.height}
                className="rounded-3xl"
              />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default FeaturedCaseStudies
